import { Trans, useLingui } from '@lingui/react/macro'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { App } from 'antd'
import { useState } from 'react'

import { useWebSignout } from '@/apis/user.api.ts'
import { Button } from '@/elements'
import { IconOffline } from '@/icons'
import { useUpdateOperatorStatus } from '@/routes/arc/-stores/operator-status.actions.ts'
import { setOperatorLocalStatus } from '@/routes/arc/-stores/operator.store.ts'
import { computeHostCallable, useHostStore } from '@/stores/host.store.tsx'

export const Route = createFileRoute('/arc/status/offline')({
  component: OfflinePage,
})

function OfflinePage() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { message } = App.useApp()
  const updateOperatorStatusMutation = useUpdateOperatorStatus()
  const signOutMutation = useWebSignout()
  const [loadingType, setLoadingType] = useState<'ADMIN' | 'AVAILABLE'>('AVAILABLE')

  return (
    <div className={'flex min-h-0 flex-1 flex-col items-center justify-center'}>
      <IconOffline />
      <div className={'my-4 font-semibold'}>
        <Trans>Currently offline</Trans>
      </div>
      <div className={'flex w-80 flex-col gap-4'}>
        <Button
          size={'large'}
          color={'primary'}
          variant={'solid'}
          loading={updateOperatorStatusMutation.isPending && loadingType === 'AVAILABLE'}
          onClick={async () => {
            if (!computeHostCallable(useHostStore.getState())) {
              message.error(t`Cannot set to Available. Hardware is not ready.`)
              return
            }
            setLoadingType('AVAILABLE')
            updateOperatorStatusMutation
              .mutateAsync({
                status: 'AVAILABLE',
                description: 'manual: operator went online from offline page',
              })
              .then(() => {
                setOperatorLocalStatus('AVAILABLE')
                void navigate({
                  to: '/arc/status/available',
                  replace: true,
                })
              })
              .catch(() => {
                message.error(t`Failed to update status`)
              })
          }}
        >
          <Trans>Available</Trans>
        </Button>
        <Button
          size={'large'}
          color={'primary'}
          variant={'outlined'}
          loading={updateOperatorStatusMutation.isPending && loadingType === 'ADMIN'}
          onClick={() => {
            setLoadingType('ADMIN')
            updateOperatorStatusMutation
              .mutateAsync({
                status: 'ADMIN',
                description: 'manual: operator switched to admin mode from offline page',
              })
              .then(() => {
                setOperatorLocalStatus('ADMIN')
                void navigate({ to: '/arc/status/admin', replace: true })
              })
              .catch(() => {
                message.error(t`Failed to update status`)
              })
          }}
        >
          <Trans>Admin</Trans>
        </Button>
        <Button
          onClick={() => {
            signOutMutation.mutate()
          }}
          color={'primary'}
          variant={'link'}
          size={'large'}
          loading={signOutMutation.isPending}
        >
          <Trans>Logout</Trans>
        </Button>
      </div>
    </div>
  )
}
